import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { get, onUnauthorized, patch, post } from './api';
import { lang, savedLang, setLang } from './i18n';
import type { Me } from './types';
import { DEFAULT_TZ } from './format';
import { forgetPushOnLogout, syncPush } from './pwa';

interface AuthState {
  me: Me | null;
  loading: boolean;
  /** Zona horaria de la iglesia (para mostrar fechas). */
  tz: string;
  setMe: (me: Me | null) => void;
  refresh: () => Promise<Me | null>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const qc = useQueryClient();

  const refresh = useCallback(async () => {
    try {
      const status = await get<{ authenticated: boolean }>('/api/auth/status');
      if (!status.authenticated) {
        setMe(null);
        return null;
      }
      const m = await get<Me>('/api/auth/me');
      // El idioma de la cuenta (para correos y avisos) y el de este navegador se mantienen iguales:
      // si este navegador no tiene preferencia, se adopta la de la cuenta; si la tiene, se guarda en la cuenta.
      const saved = savedLang();
      if (!saved && m.person.language && m.person.language !== lang()) {
        setLang(m.person.language);
        return m;
      }
      if (saved && m.person.language !== saved) void patch('/api/me/profile', { language: saved }).catch(() => undefined);
      setMe(m);
      return m;
    } catch {
      setMe(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Con sesión: si este dispositivo tenía las notificaciones activadas, se vuelve a registrar.
  const accountId = me?.account.id;
  useEffect(() => {
    if (accountId) void syncPush();
  }, [accountId]);

  // Si la sesión vence mientras se usa la app, se limpia el estado.
  useEffect(() => {
    const off = onUnauthorized(() => setMe(null));
    return () => {
      off();
    };
  }, []);

  const logout = useCallback(async () => {
    // Antes de cerrar la sesión: este dispositivo deja de recibir los avisos de la cuenta.
    await forgetPushOnLogout();
    await post('/api/auth/logout').catch(() => undefined);
    setMe(null);
    qc.clear();
  }, [qc]);

  const value = useMemo<AuthState>(
    () => ({ me, loading, tz: me?.organization?.timezone ?? DEFAULT_TZ, setMe, refresh, logout }),
    [me, loading, refresh, logout],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth fuera de AuthProvider'); // i18n-ignore
  return v;
}

/** Para pantallas que ya garantizan sesión iniciada. */
export function useMe(): Me {
  const { me } = useAuth();
  if (!me) throw new Error('Sin sesión'); // i18n-ignore
  return me;
}
